import { useTranslate } from '@tolgee/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { generatePath } from 'react-router';
import {
  useLocation,
  useMatch,
  useNavigate,
  useParams
} from 'react-router-dom';

import ConfirmModal from '@/components/confirmModal';
import GlobalLoader from '@/components/globalLoader';
import { LomavisTemplateFolderModal } from '@/lomavisSharedUi/organisms/modals/LomavisTemplateFolderModal';
import type { TemplateFolderModalMode } from '@/lomavisSharedUi/organisms/modals/LomavisTemplateFolderModal';
import { LomavisTemplateMoveFolderModal } from '@/lomavisSharedUi/organisms/modals/LomavisTemplateMoveFolderModal';
import {
  LomavisTemplateEditor,
  type TemplateEditorExportActions,
  type TemplateEditorSavePayload
} from '@/lomavisSharedUi/templates/editors/LomavisTemplateEditor/LomavisTemplateEditor';
import {
  createDefaultTemplate,
  normalizeTemplate,
  type TemplateDefinition
} from '@/lomavisSharedUi/templates/editors/LomavisTemplateEditor/templateDefinition';
import { LomavisOverlayTemplateEditorShell } from '@/lomavisSharedUi/templates/pages/LomavisOverlayTemplateEditorShell';
import { LomavisOverlayTemplateLibrary } from '@/lomavisSharedUi/templates/pages/LomavisOverlayTemplateLibrary';
import {
  getOverlayTemplateScopeKey,
  uploadOverlayTemplateImage,
  type OverlayTemplateApiScope,
  type OverlayTemplateOrdering,
  type WritableOverlayTemplateApiScope
} from '@/api/overlayTemplates';
import type {
  OverlayTemplateFolder,
  OverlayTemplateSortValue,
  OverlayTemplateSummary
} from '@/lomavisSharedUi/types/overlayTemplates';
import { getLocationDetails } from '@/redux/slices/location/locationDetails';
import {
  addOverlayTemplateFolder,
  copyOverlayTemplate,
  fetchOverlayTemplateDetail,
  fetchOverlayTemplateLibrary,
  moveOverlayTemplateToFolder,
  overlayTemplateActions,
  removeOverlayTemplate,
  removeOverlayTemplateFolder,
  renameOverlayTemplateFolder,
  saveOverlayTemplate,
  uploadOverlayTemplatePreview
} from '@/redux/slices/overlayTemplates';
import { useDispatch, useSelector } from '@/redux/store';
import { PATH_PAGE } from '@/routes/paths';
import { navigateToServerPage } from '@/utils/navigation';
import { lomavisShowToastr } from '@/utils/ui/toastr';

const ORDERING_BY_SORT: Record<
  OverlayTemplateSortValue,
  OverlayTemplateOrdering
> = {
  newest: '-pk',
  oldest: 'pk',
  nameAsc: 'title',
  nameDesc: '-title'
};

export const buildOverlayTemplateStartPostUrl = (
  locationSlug: string,
  templateUuid: string
) =>
  `${generatePath(PATH_PAGE.locationLomavisCreator, {
    locationslug: locationSlug
  })}?overlay_template_uuid=${encodeURIComponent(templateUuid)}`;

export const buildOverlayTemplateCreateUrl = (
  createPath: string,
  folderId: string | null
) => {
  if (!folderId) return createPath;
  const search = new URLSearchParams({ folder_uuid: folderId });
  return `${createPath}?${search.toString()}`;
};

const getFieldMessage = (
  fieldErrors: Record<string, unknown>,
  field: string
) => {
  const value = fieldErrors[field];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : undefined;
};

const toSummary = (template: {
  uuid: string;
  title: string;
  thumbnail_url?: string | null;
  aspect_ratio: string;
  folder_uuid?: string | null;
}): OverlayTemplateSummary => ({
  id: template.uuid,
  title: template.title,
  thumbnailUrl: template.thumbnail_url,
  aspectRatio: template.aspect_ratio,
  folderId: template.folder_uuid
});

const toFolder = (folder: {
  uuid: string;
  name: string;
}): OverlayTemplateFolder => ({
  id: folder.uuid,
  name: folder.name
});

const createTemplateDefinitionId = () =>
  `overlay-template-${
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }`;

const createEditorTemplate = (name = '') =>
  createDefaultTemplate(createTemplateDefinitionId(), name);

const getAspectRatio = (width: number, height: number) => {
  let left = Math.max(1, Math.round(width));
  let right = Math.max(1, Math.round(height));
  const originalLeft = left;
  const originalRight = right;
  while (right !== 0) [left, right] = [right, left % right];
  return `${originalLeft / left}:${originalRight / left}`;
};

const OverlayTemplatesPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t } = useTranslate();
  const { locationslug, templateUuid: routeTemplateUuid } = useParams<{
    locationslug?: string;
    templateUuid?: string;
  }>();
  const accountRoute = Boolean(useMatch('/accounts/templates/*'));
  const pickerRoute = Boolean(
    useMatch(PATH_PAGE.profile_overlay_template_account_library)
  );
  const createRoute = Boolean(
    useMatch(
      accountRoute
        ? PATH_PAGE.account_overlay_template_create
        : PATH_PAGE.profile_overlay_template_create
    )
  );
  const duplicateMatch = useMatch(
    accountRoute
      ? PATH_PAGE.account_overlay_template_duplicate
      : PATH_PAGE.profile_overlay_template_duplicate
  );
  const duplicateRoute = Boolean(duplicateMatch);
  const detailMatch = useMatch(
    accountRoute
      ? PATH_PAGE.account_overlay_template_detail
      : PATH_PAGE.profile_overlay_template_detail
  );
  const detailRoute =
    !createRoute && !duplicateRoute && !pickerRoute && Boolean(detailMatch);
  const templateUuid =
    createRoute || pickerRoute
      ? undefined
      : routeTemplateUuid ??
        duplicateMatch?.params.templateUuid ??
        detailMatch?.params.templateUuid;
  const editorRoute = createRoute || duplicateRoute || detailRoute;
  const queryFolderId = new URLSearchParams(routerLocation.search).get(
    'folder_uuid'
  );
  const editorEntryFolderId =
    (routerLocation.state as { overlayTemplateFolderId?: string | null } | null)
      ?.overlayTemplateFolderId ??
    queryFolderId ??
    null;
  const libraryScopeKey = accountRoute
    ? 'account'
    : `${pickerRoute ? 'accountPicker' : 'profile'}:${locationslug ?? ''}`;
  const location = useSelector((state) => state.locationDetails);
  const overlayTemplates = useSelector((state) => state.overlayTemplates);

  const [filterScopeKey, setFilterScopeKey] = useState(libraryScopeKey);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] =
    useState<OverlayTemplateSortValue>('newest');
  const [folderModal, setFolderModal] = useState<{
    show: boolean;
    mode: TemplateFolderModalMode;
    folder?: OverlayTemplateFolder;
  }>({ show: false, mode: 'create' });
  const [folderName, setFolderName] = useState('');
  const [templateToDelete, setTemplateToDelete] =
    useState<OverlayTemplateSummary | null>(null);
  const [templateToMove, setTemplateToMove] =
    useState<OverlayTemplateSummary | null>(null);
  const [moveToFolderId, setMoveToFolderId] = useState<string | null>(null);
  const [editorFolderId, setEditorFolderId] = useState<string | null>(null);
  const [editorTemplate, setEditorTemplate] =
    useState<TemplateDefinition>(createEditorTemplate);
  const [editorExportActions, setEditorExportActions] =
    useState<TemplateEditorExportActions | null>(null);
  const [editorConfigurationError, setEditorConfigurationError] = useState<
    string | null
  >(null);
  const [uploadingEditorImage, setUploadingEditorImage] = useState(false);
  const loadedEditorKeyRef = useRef<string | null>(null);
  const editorPreviewFileRef = useRef<File | null>(null);
  const editorSaveInFlightRef = useRef(false);

  const translate = useCallback(
    (key: string, fallback: string, params?: Record<string, string | number>) =>
      String(t(key, fallback, params)),
    [t]
  );

  useEffect(() => {
    if (!accountRoute && locationslug && location.slug !== locationslug) {
      dispatch(getLocationDetails(locationslug) as any);
    }
  }, [accountRoute, dispatch, location.slug, locationslug]);

  const scope = useMemo<OverlayTemplateApiScope | null>(() => {
    if (accountRoute) return { kind: 'account' };
    if (!location.uuid) return null;
    return pickerRoute
      ? { kind: 'accountPicker', locationUuid: location.uuid }
      : { kind: 'profile', locationUuid: location.uuid };
  }, [accountRoute, location.uuid, pickerRoute]);

  const writeScope = useMemo<WritableOverlayTemplateApiScope | null>(() => {
    if (accountRoute) return { kind: 'account' };
    if (!location.uuid) return null;
    return { kind: 'profile', locationUuid: location.uuid };
  }, [accountRoute, location.uuid]);

  const currentLibraryScopeKey = scope
    ? getOverlayTemplateScopeKey(scope)
    : null;
  const foldersBelongToCurrentScope =
    currentLibraryScopeKey !== null &&
    overlayTemplates.loadedLibraryScopeKey === currentLibraryScopeKey;

  const filtersBelongToScope = filterScopeKey === libraryScopeKey;

  useEffect(() => {
    if (filtersBelongToScope) return;
    setSelectedFolderId(null);
    setSearchValue('');
    setSortValue('newest');
    setFilterScopeKey(libraryScopeKey);
  }, [filtersBelongToScope, libraryScopeKey]);

  useEffect(() => {
    if (!scope || !filtersBelongToScope || (pickerRoute && editorRoute)) return;
    const timeout = window.setTimeout(
      () => {
        void dispatch(
          fetchOverlayTemplateLibrary({
            scope,
            folderUuid: selectedFolderId,
            search: searchValue.trim(),
            ordering: ORDERING_BY_SORT[sortValue],
            limit: 100
          })
        );
      },
      searchValue ? 250 : 0
    );
    return () => window.clearTimeout(timeout);
  }, [
    dispatch,
    editorRoute,
    filtersBelongToScope,
    pickerRoute,
    scope,
    searchValue,
    selectedFolderId,
    sortValue
  ]);

  useEffect(() => {
    dispatch(overlayTemplateActions.clearActiveTemplate());
    loadedEditorKeyRef.current = null;
    if (createRoute) {
      setEditorFolderId(editorEntryFolderId);
      setEditorTemplate(createEditorTemplate());
      setEditorConfigurationError(null);
      return;
    }
    if (editorRoute && templateUuid && scope) {
      void dispatch(fetchOverlayTemplateDetail({ uuid: templateUuid, scope }));
    }
  }, [
    createRoute,
    dispatch,
    editorEntryFolderId,
    editorRoute,
    scope,
    templateUuid
  ]);

  useEffect(() => {
    const active = overlayTemplates.activeTemplate;
    if (!active || active.uuid !== templateUuid || createRoute) return;
    const editorKey = `${active.uuid}:${duplicateRoute ? 'duplicate' : 'edit'}`;
    if (loadedEditorKeyRef.current === editorKey) return;
    loadedEditorKeyRef.current = editorKey;
    const nextName = duplicateRoute
      ? `${active.title} ${translate('overlayTemplates.copySuffix', 'copy')}`
      : active.title;
    try {
      const loadedTemplate = normalizeTemplate(active.configuration);
      setEditorTemplate({
        ...loadedTemplate,
        id: duplicateRoute ? createTemplateDefinitionId() : loadedTemplate.id,
        name: nextName
      });
      setEditorConfigurationError(null);
    } catch (error) {
      setEditorTemplate(createEditorTemplate(nextName));
      setEditorConfigurationError(
        error instanceof Error
          ? error.message
          : translate(
              'overlayTemplates.editor.invalidConfiguration',
              'This overlay configuration cannot be loaded.'
            )
      );
    }
    setEditorFolderId(active.folder_uuid ?? null);
  }, [
    createRoute,
    duplicateRoute,
    overlayTemplates.activeTemplate,
    templateUuid,
    translate
  ]);

  const listPath = useMemo(
    () =>
      accountRoute
        ? PATH_PAGE.account_overlay_templates
        : generatePath(PATH_PAGE.profile_overlay_templates, {
            locationslug: locationslug ?? ''
          }),
    [accountRoute, locationslug]
  );

  const templates = useMemo(
    () => overlayTemplates.templates.map(toSummary),
    [overlayTemplates.templates]
  );
  const folders = useMemo(
    () =>
      foldersBelongToCurrentScope
        ? overlayTemplates.folders.map(toFolder)
        : [],
    [foldersBelongToCurrentScope, overlayTemplates.folders]
  );

  const libraryLabels = useMemo(() => {
    const isPicker = pickerRoute;
    const isAccount = accountRoute;
    return {
      title: isPicker
        ? translate(
            'overlayTemplates.accountPicker.eyebrow',
            'Shared by the account'
          )
        : isAccount
        ? translate(
            'overlayTemplates.account.eyebrow',
            'Overlays · Account library'
          )
        : translate('overlayTemplates.profile.eyebrow', 'Overlays · {name}', {
            name: location.name
          }),
      description: isPicker
        ? translate(
            'overlayTemplates.accountPicker.description',
            'Choose an account overlay to start a post or clone it into this profile group.'
          )
        : translate(
            'overlayTemplates.library.description',
            'Create and organize reusable overlays for social posts.'
          ),
      back: translate('common.back', 'Back'),
      createTemplate: translate(
        'overlayTemplates.createTemplate',
        'Create overlay'
      ),
      addFromAccountLibrary: translate(
        'overlayTemplates.addFromAccountLibrary',
        'Add from account library'
      ),
      search: translate('overlayTemplates.search', 'Search overlays'),
      sort: translate('overlayTemplates.sort', 'Sort overlays'),
      emptyTitle: translate('overlayTemplates.emptyTitle', 'No overlays yet'),
      emptyDescription: translate(
        'overlayTemplates.emptyDescription',
        'Create an overlay to start building your library.'
      ),
      retry: translate('common.retry', 'Retry'),
      loading: translate('common.loading', 'Loading'),
      templateCount: (count: number) =>
        count === 1
          ? translate('overlayTemplates.templateCountOne', '1 overlay')
          : translate('overlayTemplates.templateCount', '{count} overlays', {
              count
            }),
      folderSidebar: {
        navigation: translate(
          'overlayTemplates.folderNavigation',
          'Overlay folders'
        ),
        title: translate('overlayTemplates.folders', 'Folders'),
        allTemplates: translate(
          'overlayTemplates.allTemplates',
          'All overlays'
        ),
        newFolder: translate('overlayTemplates.createFolder', 'Create folder'),
        folderActions: translate(
          'overlayTemplates.folderActions',
          'Folder actions'
        ),
        renameFolder: translate(
          'overlayTemplates.renameFolder',
          'Rename folder'
        ),
        deleteFolder: translate(
          'overlayTemplates.deleteFolder',
          'Delete folder'
        )
      },
      card: {
        edit: translate('common.edit', 'Edit'),
        changeFolder: translate(
          'overlayTemplates.changeFolder',
          'Change folder'
        ),
        duplicate: translate('overlayTemplates.duplicate', 'Duplicate'),
        clone: translate(
          'overlayTemplates.cloneInMyLibrary',
          'Clone in my library'
        ),
        delete: translate('common.delete', 'Delete'),
        startPost: translate('overlayTemplates.startPost', 'Start Post'),
        thumbnailLoading: translate(
          'overlayTemplates.thumbnailLoading',
          'Loading preview'
        ),
        thumbnailUnavailable: translate(
          'overlayTemplates.thumbnailUnavailable',
          'Preview unavailable'
        )
      }
    };
  }, [accountRoute, location.name, pickerRoute, translate]);

  const sortOptions = useMemo(
    () => [
      {
        value: 'newest' as const,
        label: translate('overlayTemplates.sortNewest', 'Newest first')
      },
      {
        value: 'oldest' as const,
        label: translate('overlayTemplates.sortOldest', 'Oldest first')
      },
      {
        value: 'nameAsc' as const,
        label: translate('overlayTemplates.sortNameAsc', 'Name A–Z')
      },
      {
        value: 'nameDesc' as const,
        label: translate('overlayTemplates.sortNameDesc', 'Name Z–A')
      }
    ],
    [translate]
  );

  const folderValidationMessage = getFieldMessage(
    overlayTemplates.fieldErrors,
    'name'
  );

  const openFolderModal = (
    mode: TemplateFolderModalMode,
    folder?: OverlayTemplateFolder
  ) => {
    dispatch(overlayTemplateActions.clearOverlayTemplateError());
    setFolderName(folder?.name ?? '');
    setFolderModal({ show: true, mode, folder });
  };

  const submitFolderModal = async () => {
    if (!writeScope) return;
    const args = { scope: writeScope };
    const action =
      folderModal.mode === 'create'
        ? addOverlayTemplateFolder({ ...args, name: folderName.trim() })
        : folderModal.mode === 'rename' && folderModal.folder
        ? renameOverlayTemplateFolder({
            ...args,
            uuid: folderModal.folder.id,
            name: folderName.trim()
          })
        : folderModal.folder
        ? removeOverlayTemplateFolder({
            ...args,
            uuid: folderModal.folder.id
          })
        : null;
    if (!action) return;
    try {
      await dispatch(action as any).unwrap();
      if (
        selectedFolderId === folderModal.folder?.id &&
        folderModal.mode === 'delete'
      ) {
        setSelectedFolderId(null);
      }
      setFolderModal({ show: false, mode: 'create' });
    } catch {
      // Field errors are rendered from Redux state in the controlled modal.
    }
  };

  const openCreateEditor = () => {
    const createPath = accountRoute
      ? PATH_PAGE.account_overlay_template_create
      : generatePath(PATH_PAGE.profile_overlay_template_create, {
          locationslug: locationslug ?? ''
        });
    navigateToServerPage(
      buildOverlayTemplateCreateUrl(createPath, selectedFolderId)
    );
  };

  const openEditEditor = (uuid: string) => {
    navigateToServerPage(
      accountRoute
        ? generatePath(PATH_PAGE.account_overlay_template_detail, {
            templateUuid: uuid
          })
        : generatePath(PATH_PAGE.profile_overlay_template_detail, {
            locationslug: locationslug ?? '',
            templateUuid: uuid
          })
    );
  };

  const openDuplicateEditor = (uuid: string) => {
    navigateToServerPage(
      accountRoute
        ? generatePath(PATH_PAGE.account_overlay_template_duplicate, {
            templateUuid: uuid
          })
        : generatePath(PATH_PAGE.profile_overlay_template_duplicate, {
            locationslug: locationslug ?? '',
            templateUuid: uuid
          })
    );
  };

  const saveEditor = async (template: TemplateDefinition = editorTemplate) => {
    if (!writeScope || !editorExportActions || editorSaveInFlightRef.current)
      return;
    editorSaveInFlightRef.current = true;
    try {
      editorPreviewFileRef.current = null;
      await editorExportActions.exportJpg();
      const previewFile = editorPreviewFileRef.current;
      if (!previewFile) {
        lomavisShowToastr(
          translate(
            'overlayTemplates.previewGenerationFailed',
            'The preview image could not be generated. Please try again.'
          ),
          'error'
        );
        return;
      }
      const generatedPreviewImageUuid = await dispatch(
        uploadOverlayTemplatePreview({
          file: previewFile,
          locationUuid: accountRoute ? undefined : location.uuid
        })
      ).unwrap();
      await dispatch(
        saveOverlayTemplate({
          uuid: detailRoute && !duplicateRoute ? templateUuid : undefined,
          scope: writeScope,
          payload: {
            title: template.name.trim(),
            folder_uuid: editorFolderId,
            configuration: template as unknown as Record<string, unknown>,
            preview_image_uuid: generatedPreviewImageUuid,
            aspect_ratio: getAspectRatio(
              template.canvas.width,
              template.canvas.height
            )
          }
        })
      ).unwrap();
      lomavisShowToastr(
        translate('overlayTemplates.saved', 'Overlay saved.'),
        'success'
      );
      navigateToServerPage(listPath);
    } catch {
      // The editor reads field errors from Redux state.
    } finally {
      editorSaveInFlightRef.current = false;
    }
  };

  const confirmTemplateDelete = async () => {
    if (!templateToDelete || !writeScope) return;
    await dispatch(
      removeOverlayTemplate({
        uuid: templateToDelete.id,
        scope: writeScope
      })
    ).unwrap();
    setTemplateToDelete(null);
  };

  const openMoveTemplateModal = (uuid: string) => {
    const template = templates.find((item) => item.id === uuid);
    if (!template) return;
    dispatch(overlayTemplateActions.clearOverlayTemplateError());
    setTemplateToMove(template);
    setMoveToFolderId(template.folderId ?? null);
  };

  const closeMoveTemplateModal = () => {
    if (overlayTemplates.movingTemplateUuid) return;
    setTemplateToMove(null);
    setMoveToFolderId(null);
    dispatch(overlayTemplateActions.clearOverlayTemplateError());
  };

  const submitMoveTemplate = async () => {
    if (!templateToMove || !writeScope) return;
    await dispatch(
      moveOverlayTemplateToFolder({
        uuid: templateToMove.id,
        folderUuid: moveToFolderId,
        scope: writeScope
      })
    ).unwrap();
    setTemplateToMove(null);
    setMoveToFolderId(null);
    await dispatch(
      fetchOverlayTemplateLibrary({
        scope,
        folderUuid: selectedFolderId,
        search: searchValue.trim(),
        ordering: ORDERING_BY_SORT[sortValue],
        limit: 100
      })
    );
    lomavisShowToastr(
      translate('overlayTemplates.folderChanged', 'Overlay folder changed.'),
      'success'
    );
  };

  const cloneFromAccount = async (uuid: string) => {
    if (!location.uuid || !writeScope) return;
    const source = templates.find((template) => template.id === uuid);
    try {
      await dispatch(
        copyOverlayTemplate({
          sourceUuid: uuid,
          sourceScope: {
            kind: 'accountPicker',
            locationUuid: location.uuid
          },
          targetScope: writeScope,
          title:
            source?.title ??
            translate('overlayTemplates.copyTitle', 'Overlay copy')
        })
      ).unwrap();
      lomavisShowToastr(
        translate(
          'overlayTemplates.clonedToProfile',
          'Overlay cloned to your library.'
        ),
        'success'
      );
      navigate(listPath);
    } catch {
      // Library error state is rendered by the shared layout.
    }
  };

  const startPost = (uuid: string) => {
    if (!locationslug) return;
    window.location.assign(
      buildOverlayTemplateStartPostUrl(locationslug, uuid)
    );
  };

  if (!scope || (!accountRoute && location.isLoading)) return <GlobalLoader />;

  if (editorRoute) {
    if (
      !createRoute &&
      (overlayTemplates.loadingTemplate ||
        overlayTemplates.activeTemplate?.uuid !== templateUuid)
    )
      return <GlobalLoader />;
    const editorMode = duplicateRoute
      ? 'duplicate'
      : createRoute
      ? 'create'
      : 'edit';
    return (
      <LomavisOverlayTemplateEditorShell
        mode={editorMode}
        templateName={editorTemplate.name}
        folderId={editorFolderId}
        folders={folders}
        templateUuid={editorMode === 'duplicate' ? null : templateUuid ?? null}
        configuration={editorTemplate as unknown as Record<string, unknown>}
        scopeLabel={
          accountRoute
            ? translate('overlayTemplates.accountLibrary', 'Account library')
            : location.name
        }
        labels={{
          title:
            editorMode === 'create'
              ? translate(
                  'overlayTemplates.editor.createTitle',
                  'Create overlay'
                )
              : editorMode === 'duplicate'
              ? translate(
                  'overlayTemplates.editor.duplicateTitle',
                  'Duplicate overlay'
                )
              : translate('overlayTemplates.editor.editTitle', 'Edit overlay'),
          description: translate(
            'overlayTemplates.editor.description',
            'Name and organize the overlay. The visual editor is loaded in the workspace.'
          ),
          templateName: translate(
            'overlayTemplates.templateName',
            'Overlay name'
          ),
          folder: translate('overlayTemplates.folder', 'Folder'),
          noFolder: translate('overlayTemplates.noFolder', 'No folder'),
          cancel: translate('common.cancel', 'Cancel'),
          save: translate('common.save', 'Save'),
          saving: translate('common.saving', 'Saving'),
          editorRegion: translate(
            'overlayTemplates.editor.region',
            'Overlay editor'
          ),
          editorPlaceholder: translate(
            'overlayTemplates.editor.placeholder',
            'The overlay editor is provided separately and mounts in this workspace.'
          ),
          templateUuid: translate(
            'overlayTemplates.editor.templateUuid',
            'Overlay UUID'
          ),
          configuration: translate(
            'overlayTemplates.editor.configuration',
            'Loaded configuration JSON'
          ),
          unassignedUuid: translate(
            'overlayTemplates.editor.unassignedUuid',
            'Assigned after the overlay is saved'
          )
        }}
        templateNameError={getFieldMessage(
          overlayTemplates.fieldErrors,
          'title'
        )}
        configurationError={
          getFieldMessage(overlayTemplates.fieldErrors, 'configuration') ??
          overlayTemplates.error ??
          editorConfigurationError ??
          undefined
        }
        isSaving={
          overlayTemplates.savingTemplate ||
          overlayTemplates.uploadingPreview ||
          uploadingEditorImage
        }
        canSave={Boolean(editorTemplate.name.trim())}
        integratedEditor
        onTemplateNameChange={(name) =>
          setEditorTemplate((template) => ({ ...template, name }))
        }
        onFolderChange={setEditorFolderId}
        onCancel={() => navigateToServerPage(listPath)}
        onSave={() => {
          saveEditor().catch(() => undefined);
        }}
      >
        <LomavisTemplateEditor
          value={editorTemplate}
          onChange={setEditorTemplate}
          onBack={() => navigateToServerPage(listPath)}
          saveFormat="json"
          onExportActionsReady={setEditorExportActions}
          onExportImage={(file, options) => {
            if (options.format === 'jpg') editorPreviewFileRef.current = file;
          }}
          onUploadImage={async (file) => {
            setUploadingEditorImage(true);
            try {
              return await uploadOverlayTemplateImage({
                file,
                locationUuid: accountRoute ? undefined : location.uuid
              });
            } finally {
              setUploadingEditorImage(false);
            }
          }}
          onSave={(payload: TemplateEditorSavePayload) => {
            if (payload.format !== 'json') return;
            setEditorTemplate(payload.template);
            saveEditor(payload.template).catch(() => undefined);
          }}
        />
      </LomavisOverlayTemplateEditorShell>
    );
  }

  return (
    <>
      <LomavisOverlayTemplateLibrary
        mode={
          pickerRoute
            ? 'accountPicker'
            : accountRoute
            ? 'account'
            : 'profileGroup'
        }
        templates={templates}
        folders={folders}
        selectedFolderId={selectedFolderId}
        searchValue={searchValue}
        sortValue={sortValue}
        sortOptions={sortOptions}
        labels={libraryLabels}
        showPageHeading={false}
        showBackAction={pickerRoute}
        showCreateAction={!pickerRoute}
        showAccountLibraryAction={!accountRoute && !pickerRoute}
        canManageFolders={!pickerRoute}
        isLoading={overlayTemplates.loadingLibrary}
        isFolderLoading={
          !foldersBelongToCurrentScope && !overlayTemplates.error
        }
        errorMessage={
          folderModal.show && folderValidationMessage
            ? null
            : overlayTemplates.error
        }
        onBack={() => navigate(listPath)}
        onCreateTemplate={openCreateEditor}
        onOpenAccountLibrary={() =>
          navigate(
            generatePath(PATH_PAGE.profile_overlay_template_account_library, {
              locationslug: locationslug ?? ''
            })
          )
        }
        onSearchChange={setSearchValue}
        onSortChange={setSortValue}
        onFolderSelect={setSelectedFolderId}
        onCreateFolder={() => openFolderModal('create')}
        onRenameFolder={(folder) => openFolderModal('rename', folder)}
        onDeleteFolder={(folder) => openFolderModal('delete', folder)}
        onEditTemplate={pickerRoute ? undefined : openEditEditor}
        onChangeTemplateFolder={pickerRoute ? undefined : openMoveTemplateModal}
        onDuplicateTemplate={pickerRoute ? undefined : openDuplicateEditor}
        onCloneTemplate={
          pickerRoute
            ? (uuid) => {
                cloneFromAccount(uuid).catch(() => undefined);
              }
            : undefined
        }
        onDeleteTemplate={
          pickerRoute
            ? undefined
            : (uuid) =>
                setTemplateToDelete(
                  templates.find((template) => template.id === uuid) ?? null
                )
        }
        onStartPost={accountRoute ? undefined : startPost}
        onRetry={() => {
          dispatch(
            fetchOverlayTemplateLibrary({
              scope,
              folderUuid: selectedFolderId,
              search: searchValue,
              ordering: ORDERING_BY_SORT[sortValue],
              limit: 100
            })
          ).catch(() => undefined);
        }}
      />

      <LomavisTemplateFolderModal
        show={folderModal.show}
        mode={folderModal.mode}
        name={folderName}
        targetName={folderModal.folder?.name}
        labels={{
          createTitle: translate(
            'overlayTemplates.folder.createTitle',
            'Create folder'
          ),
          renameTitle: translate(
            'overlayTemplates.folder.renameTitle',
            'Rename folder'
          ),
          deleteTitle: translate(
            'overlayTemplates.folder.deleteTitle',
            'Delete folder'
          ),
          folderName: translate('overlayTemplates.folder.name', 'Folder name'),
          cancel: translate('common.cancel', 'Cancel'),
          create: translate('common.create', 'Create'),
          save: translate('common.save', 'Save'),
          delete: translate('common.delete', 'Delete'),
          deleteMessage: translate(
            'overlayTemplates.folder.deleteFolderAndContentsMessage',
            'Are you sure you want to delete this folder? All overlays inside it will be permanently deleted. This action cannot be undone.'
          )
        }}
        validationMessage={folderValidationMessage}
        isSubmitting={overlayTemplates.mutatingFolder}
        onNameChange={setFolderName}
        onCancel={() => setFolderModal({ show: false, mode: 'create' })}
        onSubmit={() => {
          submitFolderModal().catch(() => undefined);
        }}
      />

      <LomavisTemplateMoveFolderModal
        show={Boolean(templateToMove)}
        templateTitle={templateToMove?.title}
        folderId={moveToFolderId}
        initialFolderId={templateToMove?.folderId ?? null}
        folders={folders}
        labels={{
          title: translate(
            'overlayTemplates.changeFolderTitle',
            'Change folder'
          ),
          template: translate('overlayTemplates.template', 'Overlay'),
          folder: translate('overlayTemplates.folder', 'Folder'),
          noFolder: translate('overlayTemplates.noFolder', 'No folder'),
          cancel: translate('common.cancel', 'Cancel'),
          save: translate('common.save', 'Save'),
          saving: translate('common.saving', 'Saving')
        }}
        validationMessage={getFieldMessage(
          overlayTemplates.fieldErrors,
          'folder_uuid'
        )}
        isSubmitting={Boolean(overlayTemplates.movingTemplateUuid)}
        onFolderChange={setMoveToFolderId}
        onCancel={closeMoveTemplateModal}
        onSubmit={() => {
          submitMoveTemplate().catch(() => undefined);
        }}
      />

      <ConfirmModal
        show={Boolean(templateToDelete)}
        handleClose={() => setTemplateToDelete(null)}
        handleConfirm={() => {
          confirmTemplateDelete().catch(() => undefined);
        }}
        title={translate('overlayTemplates.deleteTitle', 'Delete overlay')}
        message={translate(
          'overlayTemplates.deleteMessage',
          'This overlay will be permanently deleted.'
        )}
        showNextButton={false}
        confirmDisabled={Boolean(overlayTemplates.deletingTemplateUuid)}
      />
    </>
  );
};

export default OverlayTemplatesPage;
